import React, { useContext, useState } from "react";
import { FiCreditCard, FiDownload, FiFileText, FiMoon, FiSun, FiUser } from "react-icons/fi";
import { HiChevronDown, HiOutlineMenuAlt3, HiOutlineX, HiSearch } from "react-icons/hi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useBranding } from "./BrandingContext";
import { ThemeContext } from "./ThemeContext";
import { useSiteConfig } from "./SiteConfigContext";
import { dashboardPath } from "./userMenu";
import "./Header.css";

// External URLs and "open in new tab" items use a plain anchor; internal ones use the router.
function NavLink({ item, onClick, current }) {
  if (item.newTab || /^(https?:|mailto:)/i.test(item.href || "")) {
    return <a href={item.href} target={item.newTab ? "_blank" : undefined} rel="noreferrer" onClick={onClick}>{item.label}</a>;
  }
  return <Link to={item.href} aria-current={current ? "page" : undefined} onClick={onClick}>{item.label}</Link>;
}

function Header() {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { logo } = useBranding();
  const { headerMenus, userMenuItems } = useSiteConfig();
  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMarketsPage = location.pathname.startsWith("/markets");
  const signupPath = location.pathname.startsWith("/referral/") && location.search.includes("ref=")
    ? `/signup${location.search}`
    : "/signup";

  // localStorage se user check karo
  const token = localStorage.getItem("token");
  const userRaw = localStorage.getItem("user");
  const user = userRaw ? JSON.parse(userRaw) : null;

  const closeMenus = () => {
    setActiveMenu(null);
    setMobileOpen(false);
    setDepositOpen(false);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    closeMenus();
    navigate("/login");
  };

  return (
    <header className={`site-header site-header-${theme}`}>
      <div className="site-header-inner">
        <Link to="/home" className="site-brand" onClick={closeMenus}>
          <img src={logo} alt="Tillionx" />
        </Link>

        <nav className="site-nav" aria-label="Primary navigation">
          {headerMenus.map((menu) => menu.links.length === 0 ? (
            <div className={`site-menu ${menu.href?.startsWith("/markets") ? "site-market-menu" : ""} ${menu.href?.startsWith("/markets") && isMarketsPage ? "site-market-active" : ""}`} key={menu.label}>
              {menu.href ? <NavLink item={menu} onClick={closeMenus} current={menu.href.startsWith("/markets") && isMarketsPage} /> : <span>{menu.label}</span>}
            </div>
          ) : (
            <div className="site-menu" key={menu.label}>
              <button type="button" onClick={() => setActiveMenu(activeMenu === menu.label ? null : menu.label)}>
                {menu.label}<HiChevronDown className="site-menu-chevron" aria-hidden="true" />
              </button>
              {activeMenu === menu.label && (
                <div className={`site-dropdown ${menu.links.length > 3 ? "site-dropdown-wide" : ""}`}>
                  {menu.links.map(([label, href, newTab]) => <NavLink key={label} item={{ label, href, newTab }} onClick={closeMenus} />)}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="site-actions">
          <button className="site-icon-button" aria-label="Search"><HiSearch /></button>

          {token ? (
            <>
              <button className="site-rewards" type="button" onClick={() => setDepositOpen(true)}>
                <FiDownload aria-hidden="true" /> Deposit
              </button>
              <Link className="site-icon-button site-action-link" to="/wallet" aria-label="Wallet" title="Wallet"><FiCreditCard /></Link>
              <Link className="site-icon-button site-action-link" to="/transaction-history" aria-label="Orders" title="Orders"><FiFileText /></Link>
              <Link className="site-icon-button site-action-link" to="/download" aria-label="Download" title="Download"><FiDownload /></Link>
              <div
                className="site-menu site-user-menu"
                onMouseEnter={() => setActiveMenu("user")}
                onMouseLeave={() => setActiveMenu((current) => (current === "user" ? null : current))}
              >
                <button
                  type="button"
                  className="site-user-btn site-icon-button"
                  aria-label="Open dashboard"
                  aria-haspopup="menu"
                  onClick={() => { closeMenus(); navigate(dashboardPath()); }}
                >
                  <FiUser />
                </button>
                {activeMenu === "user" && (
                  <div className="site-dropdown site-user-dropdown">
                    <div className="site-user-name">{user?.name || "Account"}</div>
                    {userMenuItems.map((item) => (
                      <Link key={item.name} to={item.url || dashboardPath(item.tab)} onClick={closeMenus}>{item.name}</Link>
                    ))}
                    <button onClick={logout} className="site-logout-btn">Log Out</button>
                  </div>
                )}
              </div>
            </>
          ) : (
            // Logged out — Login/Signup dikhao
            <>
              <Link className="site-login" to="/login">Log In</Link>
              <Link className="site-signup" to={signupPath}>Sign Up</Link>
            </>
          )}

          <button className="site-icon-button" aria-label="Toggle theme" onClick={toggleTheme}>
            {theme === "dark" ? <FiSun /> : <FiMoon />}
          </button>
          <button className="site-mobile-toggle" aria-label={mobileOpen ? "Close menu" : "Open menu"} onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <HiOutlineX /> : <HiOutlineMenuAlt3 />}
          </button>
        </div>
      </div>

      {depositOpen && (
        <>
          <button
            className="site-drawer-backdrop"
            type="button"
            aria-label="Close deposit options"
            onClick={() => setDepositOpen(false)}
          />
          <aside className="site-deposit-drawer" role="dialog" aria-modal="true" aria-labelledby="site-deposit-title">
            <div className="site-deposit-drawer-header">
              <span className="site-deposit-currency">INR <HiChevronDown aria-hidden="true" /></span>
              <button
                className="site-icon-button"
                type="button"
                aria-label="Close deposit options"
                onClick={() => setDepositOpen(false)}
              >
                <HiOutlineX />
              </button>
            </div>
            <h2 id="site-deposit-title">Deposit</h2>
            <p className="site-deposit-section-label">I don't have crypto assets</p>
            <Link className="site-deposit-option" to="/p2ptrading" onClick={closeMenus}>
              <span className="site-deposit-option-icon"><FiCreditCard /></span>
              <span><strong>P2P with INR</strong><small>Buy crypto directly from other users via secure payment methods</small></span>
            </Link>
            <p className="site-deposit-section-label">I have crypto assets</p>
            <Link className="site-deposit-option" to="/depositCrypto" onClick={closeMenus}>
              <span className="site-deposit-option-icon"><FiDownload /></span>
              <span><strong>Deposit Crypto</strong><small>Send crypto to your Tillionx account</small></span>
            </Link>
          </aside>
        </>
      )}

      {mobileOpen && (
        <div className="site-mobile-panel">
          {headerMenus.map((menu) => menu.links.length === 0 ? (
            menu.href ? <NavLink key={menu.label} item={menu} onClick={closeMenus} /> : <span key={menu.label}>{menu.label}</span>
          ) : (
            <details key={menu.label}>
              <summary>{menu.label}</summary>
              {menu.links.map(([label, href, newTab]) => <NavLink key={label} item={{ label, href, newTab }} onClick={closeMenus} />)}
            </details>
          ))}
          {token ? (
            <>
              <Link to="/rewards" onClick={closeMenus}>Rewards</Link>
              <Link to="/account" onClick={closeMenus}>Account</Link>
              <Link to="/wallet" onClick={closeMenus}>Wallet</Link>
              <button onClick={logout} className="site-logout-btn">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={closeMenus}>Log In</Link>
              <Link to={signupPath} onClick={closeMenus}>Sign Up</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}

export default Header;
